import { Zap } from "lucide-react";

import { cn } from "@monorepo-apps/ui/src/lib/utils";

import type { CheckpointEntry, TimelineEntry, WeekEntry } from "@/routes/_app/-timeline-v2.data";

export type WeekNumber = 1 | 2 | 3 | 4;
export type MilestoneKey = WeekNumber | string;

interface HorizontalTrackProps {
  entries: TimelineEntry[];
  selected: MilestoneKey | null;
  currentWeek: 1 | 2 | 3 | 4 | null;
  onSelect: (key: MilestoneKey) => void;
}

export function HorizontalTrack({
  entries,
  selected,
  currentWeek,
  onSelect,
}: HorizontalTrackProps) {
  return (
    <div data-slot="horizontal-track" className="relative">
      <PhaseTags />
      <TrackBody
        entries={entries}
        selected={selected}
        currentWeek={currentWeek}
        onSelect={onSelect}
      />
    </div>
  );
}

function PhaseTags() {
  return (
    <div className="mb-3 flex items-baseline justify-between gap-4 px-2">
      <PhaseTag tone="discovery" label="Phase 01 · Discovery" window="May 4 – May 17" />
      <PhaseTag tone="build" label="Phase 02 · Build-spec" window="May 18 – May 31" />
    </div>
  );
}

function PhaseTag({
  tone,
  label,
  window,
}: {
  tone: "discovery" | "build";
  label: string;
  window: string;
}) {
  return (
    <div className="flex items-baseline gap-3">
      <PhaseTagMarker tone={tone} />
      <PhaseTagLabel tone={tone} label={label} window={window} />
    </div>
  );
}

function PhaseTagMarker({ tone }: { tone: "discovery" | "build" }) {
  return (
    <span
      aria-hidden
      className={cn(
        "h-1.5 w-1.5 rotate-45 rounded-[1px]",
        tone === "discovery" ? "bg-[oklch(0.55_0.17_190)]" : "bg-[oklch(0.74_0.16_80)]"
      )}
    />
  );
}

function PhaseTagLabel({
  tone,
  label,
  window,
}: {
  tone: "discovery" | "build";
  label: string;
  window: string;
}) {
  return (
    <span
      className={cn(
        "font-data text-[10px] tracking-[0.2em] uppercase",
        tone === "discovery" ? "text-[oklch(0.45_0.18_195)]" : "text-[oklch(0.5_0.18_60)]"
      )}
    >
      <span className="font-semibold">{label}</span>
      <span className="text-muted-foreground/70 ml-2 tracking-[0.18em]">{window}</span>
    </span>
  );
}

function TrackBody({ entries, selected, currentWeek, onSelect }: HorizontalTrackProps) {
  return (
    <div className="relative px-4 pt-10 pb-2">
      <TrackLine />
      <TrackNodes
        entries={entries}
        selected={selected}
        currentWeek={currentWeek}
        onSelect={onSelect}
      />
    </div>
  );
}

function TrackLine() {
  return (
    <span
      aria-hidden
      className="pointer-events-none absolute top-[54px] right-4 left-4 h-px"
      style={{
        background:
          "linear-gradient(to right, oklch(0.55 0.17 190 / 0.55) 0%, oklch(0.55 0.17 190 / 0.55) 25%, oklch(0.74 0.17 120 / 0.55) 40%, oklch(0.74 0.16 80 / 0.55) 55%, oklch(0.74 0.16 80 / 0.55) 100%)",
      }}
    />
  );
}

function TrackNodes({ entries, selected, currentWeek, onSelect }: HorizontalTrackProps) {
  return (
    <div className="relative flex items-start justify-between gap-2">
      {entries.map((entry) => (
        <NodeColumn
          key={milestoneKey(entry)}
          entry={entry}
          selected={selected === milestoneKey(entry)}
          isCurrent={entry.kind === "week" && entry.number === currentWeek}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
}

function milestoneKey(entry: TimelineEntry): MilestoneKey {
  return entry.kind === "week" ? entry.number : entry.id;
}

function NodeColumn({
  entry,
  selected,
  isCurrent,
  onSelect,
}: {
  entry: TimelineEntry;
  selected: boolean;
  isCurrent: boolean;
  onSelect: (key: MilestoneKey) => void;
}) {
  if (entry.kind === "checkpoint") {
    return <CheckpointColumn entry={entry} selected={selected} onSelect={onSelect} />;
  }
  return <WeekColumn entry={entry} selected={selected} isCurrent={isCurrent} onSelect={onSelect} />;
}

function WeekColumn({
  entry,
  selected,
  isCurrent,
  onSelect,
}: {
  entry: WeekEntry;
  selected: boolean;
  isCurrent: boolean;
  onSelect: (key: MilestoneKey) => void;
}) {
  const tone: "discovery" | "build" = entry.number <= 2 ? "discovery" : "build";
  return (
    <button
      type="button"
      onClick={() => onSelect(entry.number)}
      aria-pressed={selected}
      className="group/node relative flex flex-1 cursor-pointer flex-col items-center gap-2 text-center"
    >
      <WeekNodeDot number={entry.number} tone={tone} selected={selected} isCurrent={isCurrent} />
      <NodeMeta window={entry.window} tone={tone} isCurrent={isCurrent} />
      <NodeTitle title={entry.title} selected={selected} />
    </button>
  );
}

function WeekNodeDot({
  number,
  tone,
  selected,
  isCurrent,
}: {
  number: 1 | 2 | 3 | 4;
  tone: "discovery" | "build";
  selected: boolean;
  isCurrent: boolean;
}) {
  return (
    <span aria-hidden className="relative z-10 flex h-7 w-7 items-center justify-center">
      {isCurrent ? <PulseRing tone={tone} /> : null}
      <WeekDotInner number={number} tone={tone} selected={selected} />
    </span>
  );
}

function PulseRing({ tone }: { tone: "discovery" | "build" }) {
  return (
    <span
      aria-hidden
      className={cn(
        "absolute inset-0 animate-ping rounded-full",
        tone === "discovery" ? "bg-[oklch(0.55_0.17_190_/_0.4)]" : "bg-[oklch(0.66_0.16_60_/_0.45)]"
      )}
    />
  );
}

function WeekDotInner({
  number,
  tone,
  selected,
}: {
  number: 1 | 2 | 3 | 4;
  tone: "discovery" | "build";
  selected: boolean;
}) {
  return (
    <span
      className={cn(
        "bg-background relative z-[1] flex h-7 w-7 items-center justify-center rounded-full border-2 shadow-[0_0_0_4px_var(--background)] transition-all",
        "group-hover/node:scale-110",
        tone === "discovery"
          ? "border-[oklch(0.55_0.17_190)] text-[oklch(0.4_0.18_195)]"
          : "border-[oklch(0.66_0.16_60)] text-[oklch(0.4_0.18_60)]",
        selected &&
          (tone === "discovery"
            ? "scale-110 bg-[oklch(0.55_0.17_190)] text-white shadow-[0_0_0_4px_var(--background),0_0_0_6px_oklch(0.55_0.17_190_/_0.3)]"
            : "scale-110 bg-[oklch(0.66_0.16_60)] text-white shadow-[0_0_0_4px_var(--background),0_0_0_6px_oklch(0.66_0.16_60_/_0.3)]")
      )}
    >
      <span className="font-data text-[10px] font-semibold tracking-wide">W{number}</span>
    </span>
  );
}

function NodeMeta({
  window,
  tone,
  isCurrent,
}: {
  window: string;
  tone: "discovery" | "build";
  isCurrent: boolean;
}) {
  return (
    <span
      className={cn(
        "font-data flex items-center gap-1.5 text-[9px] tracking-[0.16em] whitespace-nowrap uppercase",
        tone === "discovery"
          ? "text-[oklch(0.45_0.18_195_/_0.85)]"
          : "text-[oklch(0.5_0.18_60_/_0.85)]"
      )}
    >
      {isCurrent ? <CurrentMarker tone={tone} /> : null}
      {window}
    </span>
  );
}

function CurrentMarker({ tone }: { tone: "discovery" | "build" }) {
  return (
    <span
      aria-hidden
      className={cn(
        "animate-status-pulse inline-block h-1.5 w-1.5 rounded-full",
        tone === "discovery" ? "bg-[oklch(0.55_0.17_190)]" : "bg-[oklch(0.66_0.16_60)]"
      )}
    />
  );
}

function NodeTitle({ title, selected }: { title: string; selected: boolean }) {
  return (
    <span
      className={cn(
        "max-w-[140px] text-[11px] leading-snug tracking-tight transition-colors",
        selected ? "text-foreground font-medium" : "text-muted-foreground"
      )}
    >
      {title}
    </span>
  );
}

function CheckpointColumn({
  entry,
  selected,
  onSelect,
}: {
  entry: CheckpointEntry;
  selected: boolean;
  onSelect: (key: MilestoneKey) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(entry.id)}
      aria-pressed={selected}
      className="group/node relative flex flex-1 cursor-pointer flex-col items-center gap-2 text-center"
    >
      <CheckpointNodeDot tone={entry.tone} selected={selected} />
      <CheckpointMeta date={entry.date} tone={entry.tone} label={checkpointEyebrow(entry)} />
      <CheckpointTitle selected={selected} label={checkpointShortLabel(entry)} />
    </button>
  );
}

function checkpointEyebrow(entry: CheckpointEntry): string {
  return entry.tone === "transition" ? "Inflection" : "Estimation";
}

function checkpointShortLabel(entry: CheckpointEntry): string {
  return entry.tone === "transition" ? "Owner checkpoint" : "Estimation refresh";
}

function CheckpointNodeDot({
  tone,
  selected,
}: {
  tone: "transition" | "build";
  selected: boolean;
}) {
  return (
    <span
      aria-hidden
      className={cn(
        "bg-background relative z-10 flex h-8 w-8 items-center justify-center shadow-[0_0_0_5px_var(--background)] transition-transform group-hover/node:scale-110",
        selected && "scale-110"
      )}
    >
      <CheckpointDiamondShape tone={tone} selected={selected} />
      <Zap
        className={cn(
          "relative h-3.5 w-3.5",
          selected
            ? "text-white"
            : tone === "transition"
              ? "text-[oklch(0.4_0.18_195)]"
              : "text-[oklch(0.4_0.18_60)]"
        )}
      />
    </span>
  );
}

function CheckpointDiamondShape({
  tone,
  selected,
}: {
  tone: "transition" | "build";
  selected: boolean;
}) {
  const isTransition = tone === "transition";
  return (
    <span
      aria-hidden
      className={cn(
        "absolute inset-0 rotate-45 rounded-[3px] border-2 transition-colors",
        isTransition ? "border-[oklch(0.55_0.17_190)]" : "border-[oklch(0.66_0.16_60)]",
        selected
          ? isTransition
            ? "bg-[oklch(0.55_0.17_190)] shadow-[0_0_0_5px_oklch(0.55_0.17_190_/_0.25)]"
            : "bg-[oklch(0.66_0.16_60)] shadow-[0_0_0_5px_oklch(0.66_0.16_60_/_0.25)]"
          : isTransition
            ? "bg-[oklch(0.55_0.17_190_/_0.14)]"
            : "bg-[oklch(0.66_0.16_60_/_0.14)]"
      )}
    />
  );
}

function CheckpointMeta({
  date,
  tone,
  label,
}: {
  date: string;
  tone: "transition" | "build";
  label: string;
}) {
  return (
    <span
      className={cn(
        "font-data text-[9px] tracking-[0.18em] whitespace-nowrap uppercase",
        tone === "transition"
          ? "text-[oklch(0.45_0.18_195_/_0.85)]"
          : "text-[oklch(0.5_0.18_60_/_0.85)]"
      )}
    >
      {label} · {date}
    </span>
  );
}

function CheckpointTitle({ selected, label }: { selected: boolean; label: string }) {
  return (
    <span
      className={cn(
        "max-w-[140px] text-[11px] leading-snug tracking-tight transition-colors",
        selected ? "text-foreground font-medium" : "text-muted-foreground"
      )}
    >
      {label}
    </span>
  );
}
